import {
  Bot,
  ChevronDown,
  GitFork,
  LoaderCircle,
  MessageSquare,
  Minus,
  Plus,
  Send,
  Sparkles,
  Star,
  Trash2,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  createAiConversation,
  deleteAiConversation,
  getAiConversationMessages,
  getAiConversations,
  sendAiConversationMessage,
} from '../api.js'
import { useAuth } from '../auth-context.js'

const DETAIL_QUESTIONS = [
  '这个项目是做什么的？',
  '这个项目适合新手学习吗？',
  '适合写进简历吗？',
  '有哪些技术亮点？',
  '我应该怎么学习它？',
]

const GENERAL_WELCOME_QUESTIONS = [
  '推荐几个适合写进简历的项目',
  '我想找 AI 应用开发项目',
  '有哪些值得关注的 TypeScript 项目？',
  '推荐适合新手学习的开源项目',
]

const MOBILE_BREAKPOINT = 768
const CHAT_POSITION_STORAGE_KEY = 'ai_chat_window_position'
const CHAT_ZOOM_STORAGE_KEY = 'ai_chat_window_zoom'
const BUTTON_SIZE = 56
const DESKTOP_SAFE_MARGIN = 24
const MOBILE_SAFE_MARGIN = 16
const BUTTON_SNAP_TRANSITION_MS = 180
const DRAG_CLICK_SUPPRESS_MS = 320
const CHAT_WINDOW_SIZE = { width: 420, height: 620 }
const CHAT_ZOOM_LEVELS = [0.8, 0.9, 1, 1.1, 1.25, 1.5]
const DEFAULT_CHAT_ZOOM = 1
const DESKTOP_EDGE_GAP = 22

function compactNumber(value) {
  return new Intl.NumberFormat('zh-CN', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(Number(value || 0))
}

function assistantPage(pathname) {
  if (pathname.startsWith('/repos/')) return 'repo_detail'
  if (pathname.startsWith('/github-analysis/share/')) return 'github_analysis_share'
  if (pathname.startsWith('/github-analysis')) return 'github_analysis'
  if (pathname.startsWith('/my-github')) return 'user_github_analysis'
  if (pathname === '/') return 'home'
  return 'ranking'
}

function isMobileViewport() {
  return window.innerWidth <= MOBILE_BREAKPOINT
}

function viewportBounds() {
  return {
    width: document.documentElement.clientWidth || window.innerWidth,
    height: document.documentElement.clientHeight || window.innerHeight,
  }
}

function currentButtonSafeMargin() {
  return isMobileViewport() ? MOBILE_SAFE_MARGIN : DESKTOP_SAFE_MARGIN
}

function normalizeButtonSize(size) {
  const width = Number.isFinite(size?.width) ? size.width : BUTTON_SIZE
  const height = Number.isFinite(size?.height) ? size.height : BUTTON_SIZE
  return { width, height }
}

function clampButtonPosition(position, size) {
  const viewport = viewportBounds()
  const buttonSize = normalizeButtonSize(size)
  return {
    x: Math.min(
      Math.max(0, position.x),
      Math.max(0, viewport.width - buttonSize.width),
    ),
    y: Math.min(
      Math.max(0, position.y),
      Math.max(0, viewport.height - buttonSize.height),
    ),
  }
}

function snapButtonPosition(position, size) {
  const viewport = viewportBounds()
  const buttonSize = normalizeButtonSize(size)
  const safeMargin = currentButtonSafeMargin()
  const centerX = position.x + buttonSize.width / 2
  const finalX = centerX < viewport.width / 2
    ? safeMargin
    : viewport.width - buttonSize.width - safeMargin

  return clampButtonPosition({
    x: finalX,
    y: position.y,
  }, buttonSize)
}

function chatWindowDisplaySize(zoom) {
  return {
    width: CHAT_WINDOW_SIZE.width * zoom,
    height: CHAT_WINDOW_SIZE.height * zoom,
  }
}

function clampChatWindowPosition(position, zoom) {
  const viewport = viewportBounds()
  const displaySize = chatWindowDisplaySize(zoom)
  return {
    x: Math.min(
      Math.max(0, position.x),
      Math.max(0, viewport.width - displaySize.width),
    ),
    y: Math.min(
      Math.max(0, position.y),
      Math.max(0, viewport.height - displaySize.height),
    ),
  }
}

function getDefaultChatWindowPosition(zoom) {
  const viewport = viewportBounds()
  const displaySize = chatWindowDisplaySize(zoom)
  return {
    x: Math.max(0, viewport.width - displaySize.width - DESKTOP_EDGE_GAP),
    y: Math.max(0, viewport.height - displaySize.height - DESKTOP_EDGE_GAP),
  }
}

function readStoredChatWindowZoom() {
  try {
    const storedZoom = Number(
      JSON.parse(window.localStorage.getItem(CHAT_ZOOM_STORAGE_KEY)),
    )
    return CHAT_ZOOM_LEVELS.includes(storedZoom)
      ? storedZoom
      : DEFAULT_CHAT_ZOOM
  } catch {
    return DEFAULT_CHAT_ZOOM
  }
}

function readStoredChatWindowPosition(zoom) {
  try {
    const storedPosition = JSON.parse(
      window.localStorage.getItem(CHAT_POSITION_STORAGE_KEY),
    )
    if (
      !Number.isFinite(storedPosition?.x)
      || !Number.isFinite(storedPosition?.y)
    ) {
      return null
    }
    return clampChatWindowPosition(storedPosition, zoom)
  } catch {
    return null
  }
}

function saveLocalPreference(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // The assistant remains usable when storage is unavailable.
  }
}

function removePointerListeners(interaction) {
  if (!interaction?.listeners) return
  window.removeEventListener('pointermove', interaction.listeners.move)
  window.removeEventListener('pointerup', interaction.listeners.finish)
  window.removeEventListener('pointercancel', interaction.listeners.cancel)
}

function AIAssistantAvatar({ size = 'large' }) {
  return (
    <span className={`ai-assistant-avatar is-${size}`} aria-hidden="true">
      <Bot size={size === 'large' ? 28 : 20} strokeWidth={2.2} />
      <i />
    </span>
  )
}

function RecommendationCard({ repo, onOpen }) {
  return (
    <Link
      className="ai-repo-card"
      to={repo.detailUrl || `/repos/${repo.id}`}
      onClick={onOpen}
    >
      <span className="ai-repo-card-heading">
        <strong>{repo.fullName}</strong>
        <small>{repo.language || '未标注'}</small>
      </span>
      <p>{repo.description || '该项目暂未提供 GitHub 简介。'}</p>
      <span className="ai-repo-reason">{repo.reason}</span>
      <span className="ai-repo-metrics">
        <span><Star size={13} /> {compactNumber(repo.stars)}</span>
        <span><GitFork size={13} /> {compactNumber(repo.forks)}</span>
      </span>
    </Link>
  )
}

function ActionCards({ actions = [], onAction }) {
  const visibleActions = Array.isArray(actions) ? actions.slice(0, 4) : []
  if (!visibleActions.length) return null
  return (
    <div className="ai-action-cards">
      {visibleActions.map((action, index) => (
        <button
          className="ai-action-card"
          key={`${action.type}-${action.label}-${index}`}
          type="button"
          onClick={() => onAction(action)}
        >
          {action.label || '打开'}
        </button>
      ))}
    </div>
  )
}

function renderInlineMarkdown(text, keyPrefix) {
  const parts = String(text).split(/(`[^`]+`)/g)
  return parts.map((part, index) => {
    if (part.startsWith('`') && part.endsWith('`') && part.length > 1) {
      return <code key={`${keyPrefix}-code-${index}`}>{part.slice(1, -1)}</code>
    }
    return <span key={`${keyPrefix}-text-${index}`}>{part}</span>
  })
}

function renderTextMarkdown(text, keyPrefix) {
  const lines = String(text).replace(/\r\n/g, '\n').split('\n')
  const blocks = []
  let index = 0

  while (index < lines.length) {
    const line = lines[index]
    const trimmed = line.trim()

    if (!trimmed) {
      index += 1
      continue
    }

    const heading = trimmed.match(/^(#{1,4})\s+(.+)$/)
    if (heading) {
      blocks.push(
        <h4 key={`${keyPrefix}-heading-${index}`}>
          {renderInlineMarkdown(heading[2], `${keyPrefix}-heading-${index}`)}
        </h4>,
      )
      index += 1
      continue
    }

    if (/^(-|\*)\s+/.test(trimmed) || /^\d+\.\s+/.test(trimmed)) {
      const items = []
      const ordered = /^\d+\.\s+/.test(trimmed)

      while (index < lines.length) {
        const itemLine = lines[index].trim()
        const itemMatch = itemLine.match(ordered ? /^\d+\.\s+(.+)$/ : /^(-|\*)\s+(.+)$/)
        if (!itemMatch) break
        items.push(
          <li key={`${keyPrefix}-item-${index}`}>
            {renderInlineMarkdown(ordered ? itemMatch[1] : itemMatch[2], `${keyPrefix}-item-${index}`)}
          </li>,
        )
        index += 1
      }

      const ListTag = ordered ? 'ol' : 'ul'
      blocks.push(<ListTag key={`${keyPrefix}-list-${index}`}>{items}</ListTag>)
      continue
    }

    const paragraph = [trimmed]
    index += 1
    while (index < lines.length) {
      const nextLine = lines[index].trim()
      if (
        !nextLine
        || /^(#{1,4})\s+/.test(nextLine)
        || /^(-|\*)\s+/.test(nextLine)
        || /^\d+\.\s+/.test(nextLine)
      ) {
        break
      }
      paragraph.push(nextLine)
      index += 1
    }

    blocks.push(
      <p key={`${keyPrefix}-paragraph-${index}`}>
        {renderInlineMarkdown(paragraph.join('\n'), `${keyPrefix}-paragraph-${index}`)}
      </p>,
    )
  }

  return blocks
}

function MarkdownMessage({ content }) {
  const segments = String(content || '').split('```')
  const rendered = segments.flatMap((segment, index) => {
    if (index % 2 === 1) {
      const codeLines = segment.replace(/^\n/, '').replace(/\n$/, '').split('\n')
      const language = /^[a-zA-Z0-9_-]+$/.test(codeLines[0]?.trim() || '')
        ? codeLines.shift()
        : ''
      return [
        <pre key={`code-${index}`} data-language={language || undefined}>
          <code>{codeLines.join('\n')}</code>
        </pre>,
      ]
    }
    return renderTextMarkdown(segment, `text-${index}`)
  })

  return <div className="ai-message-markdown">{rendered}</div>
}

function WelcomePanel({
  disabled,
  isRepoContext,
  onQuestion,
  questions,
  repoName,
}) {
  return (
    <div className="ai-assistant-welcome">
      <span className="ai-assistant-welcome-icon">
        <Sparkles size={18} />
      </span>
      <h3>{isRepoContext ? '想了解这个项目？直接问我' : '想发现合适的开源项目？直接问我'}</h3>
      <p>
        {isRepoContext
          ? '我可以帮你分析项目亮点、学习价值、简历包装和技术路线。'
          : '我可以按技术方向、学习目标和求职准备，帮你筛选值得关注的 GitHub 项目。'}
      </p>
      {repoName && <span className="ai-assistant-welcome-context">{repoName}</span>}
      <div className="ai-assistant-welcome-chips">
        {questions.map((question) => (
          <button
            type="button"
            key={question}
            onClick={() => onQuestion(question)}
            disabled={disabled}
          >
            {question}
          </button>
        ))}
      </div>
    </div>
  )
}

function normalizeConversation(conversation = {}) {
  return {
    id: String(conversation.id),
    title: conversation.title || '新对话',
    createdAt: conversation.createdAt || conversation.created_at || '',
    updatedAt: conversation.updatedAt || conversation.updated_at || '',
  }
}

function normalizeMessage(message = {}) {
  return {
    id: String(message.id || `${message.role}-${Date.now()}`),
    role: message.role,
    content: message.content || '',
    actions: Array.isArray(message.actions) ? message.actions : [],
    recommendedRepos: Array.isArray(message.recommendedRepos) ? message.recommendedRepos : [],
  }
}

function visibleReposFromPage() {
  if (typeof document === 'undefined') return []
  const links = [...document.querySelectorAll('a[href*="/repos/"], a[href*="github.com/"]')]
  const seen = new Set()
  return links.map((link) => {
    const href = link.getAttribute('href') || ''
    const text = (link.textContent || '').trim().replace(/\s+/g, ' ')
    const githubMatch = href.match(/github\.com\/([^/\s]+\/[^/#?\s]+)/i)
    const repoPathMatch = href.match(/\/repos\/([^/#?\s]+)/i)
    const fullName = githubMatch?.[1] || (text.includes('/') ? text.split(/\s+/)[0] : '')
    const key = fullName || href || text
    if (!key || seen.has(key)) return null
    seen.add(key)
    return {
      id: repoPathMatch?.[1] || '',
      name: fullName ? fullName.split('/').pop() : text,
      fullName,
      githubUrl: githubMatch ? `https://github.com/${githubMatch[1]}` : '',
      description: '',
      detailPath: repoPathMatch ? `/repos/${repoPathMatch[1]}` : '',
    }
  }).filter(Boolean).slice(0, 12)
}

function conversationTime(conversation) {
  const value = conversation.updatedAt || conversation.createdAt
  const timestamp = value ? new Date(value).getTime() : 0
  return Number.isFinite(timestamp) ? timestamp : 0
}

function sortConversations(conversations) {
  const safeConversations = Array.isArray(conversations) ? conversations : []
  return [...safeConversations].sort((left, right) => (
    conversationTime(right) - conversationTime(left)
    || Number(right.id) - Number(left.id)
  ))
}

function upsertConversation(conversations, conversation) {
  const normalized = normalizeConversation(conversation)
  const safeConversations = Array.isArray(conversations) ? conversations : []
  const next = safeConversations.filter((item) => item.id !== normalized.id)
  next.push(normalized)
  return sortConversations(next)
}

function dateKey(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

function conversationDateLabel(value) {
  const date = value ? new Date(value) : null
  if (!date || Number.isNaN(date.getTime())) return '未知时间'

  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)
  if (dateKey(date) === dateKey(today)) return '今天'
  if (dateKey(date) === dateKey(yesterday)) return '昨天'
  return dateKey(date)
}

function groupConversationsByDate(conversations) {
  return sortConversations(conversations).reduce((groups, conversation) => {
    const label = conversationDateLabel(
      conversation.updatedAt || conversation.createdAt,
    )
    const currentGroup = groups.find((group) => group.label === label)
    if (currentGroup) {
      currentGroup.items.push(conversation)
    } else {
      groups.push({ label, items: [conversation] })
    }
    return groups
  }, [])
}

function AIAssistantWidget({
  hideFloatingButton = false,
  onOpenChange,
  open: controlledOpen,
  repoContext,
}) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const [internalOpen, setInternalOpen] = useState(false)
  const isOpenControlled = controlledOpen !== undefined
  const open = isOpenControlled ? controlledOpen : internalOpen
  const setOpen = useCallback((nextOpen) => {
    const resolvedOpen = typeof nextOpen === 'function'
      ? nextOpen(open)
      : nextOpen
    if (isOpenControlled) {
      onOpenChange?.(resolvedOpen)
      return
    }
    setInternalOpen(resolvedOpen)
  }, [isOpenControlled, onOpenChange, open])
  const [conversations, setConversations] = useState([])
  const [activeConversationId, setActiveConversationId] = useState('')
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  const [messagesLoading, setMessagesLoading] = useState(false)
  const [conversationActionLoading, setConversationActionLoading] = useState(false)
  const [deletingConversationId, setDeletingConversationId] = useState('')
  const [error, setError] = useState('')
  const [isMobile, setIsMobile] = useState(isMobileViewport)
  const [buttonPosition, setButtonPosition] = useState(null)
  const [chatWindowPosition, setChatWindowPosition] = useState(null)
  const [chatWindowZoom, setChatWindowZoom] = useState(DEFAULT_CHAT_ZOOM)
  const [dragging, setDragging] = useState(false)
  const [snapping, setSnapping] = useState(false)
  const [draggingWindow, setDraggingWindow] = useState(false)
  const messageListRef = useRef(null)
  const requestRef = useRef(null)
  const dragRef = useRef(null)
  const chatDragRef = useRef(null)
  const suppressClickRef = useRef(false)
  const suppressClickTimerRef = useRef(null)
  const snapTimerRef = useRef(null)
  const skipNextMessageLoadRef = useRef('')

  const suggestedQuestions = useMemo(() => (
    repoContext
      ? DETAIL_QUESTIONS
      : GENERAL_WELCOME_QUESTIONS
  ), [repoContext])

  const conversationGroups = useMemo(() => (
    groupConversationsByDate(Array.isArray(conversations) ? conversations : [])
  ), [conversations])
  const safeConversationGroups = useMemo(() => (
    Array.isArray(conversationGroups) ? conversationGroups.filter(Boolean) : []
  ), [conversationGroups])
  const activeRepoName = repoContext?.fullName || repoContext?.name || ''

  useEffect(() => () => {
    requestRef.current?.abort()
    removePointerListeners(dragRef.current)
    removePointerListeners(chatDragRef.current)
    window.clearTimeout(suppressClickTimerRef.current)
    window.clearTimeout(snapTimerRef.current)
  }, [])

  useEffect(() => {
    if (isMobile) return
    const storedZoom = readStoredChatWindowZoom()
    setChatWindowZoom(storedZoom)
    setChatWindowPosition(readStoredChatWindowPosition(storedZoom))
  }, [isMobile])

  useEffect(() => {
    const handleResize = () => {
      const mobile = isMobileViewport()
      setIsMobile(mobile)
      setButtonPosition((currentPosition) => {
        if (!currentPosition) return currentPosition
        return clampButtonPosition(currentPosition)
      })

      if (mobile) return

      setChatWindowPosition((currentPosition) => {
        if (!currentPosition) {
          return open ? getDefaultChatWindowPosition(chatWindowZoom) : null
        }
        const nextPosition = clampChatWindowPosition(
          currentPosition,
          chatWindowZoom,
        )
        if (
          nextPosition.x !== currentPosition.x
          || nextPosition.y !== currentPosition.y
        ) {
          saveLocalPreference(CHAT_POSITION_STORAGE_KEY, nextPosition)
        }
        return nextPosition
      })
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [chatWindowZoom, open])

  useEffect(() => {
    if (!open || !messageListRef.current) return
    const frame = window.requestAnimationFrame(() => {
      messageListRef.current?.scrollTo({
        top: messageListRef.current.scrollHeight,
        behavior: 'smooth',
      })
    })
    return () => window.cancelAnimationFrame(frame)
  }, [loading, messages, open])

  useEffect(() => {
    if (!open || authLoading) return undefined
    if (!user) {
      setConversations([])
      setActiveConversationId('')
      setMessages([])
      setInput('')
      setError('')
      return undefined
    }

    const controller = new AbortController()
    setHistoryLoading(true)
    setError('')
    getAiConversations(controller.signal)
      .then(({ items = [] } = {}) => {
        const safeItems = Array.isArray(items) ? items : []
        const nextConversations = sortConversations(
          safeItems.map(normalizeConversation),
        )
        setConversations(nextConversations)
        setActiveConversationId((currentId) => (
          currentId && nextConversations.some((item) => item.id === currentId)
            ? currentId
            : nextConversations[0]?.id || ''
        ))
      })
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') {
          setError(requestError.message || '读取 AI 对话历史失败')
          setConversations([])
          setActiveConversationId('')
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setHistoryLoading(false)
      })

    return () => controller.abort()
  }, [authLoading, open, user])

  useEffect(() => {
    if (!open || !user || !activeConversationId) {
      setMessages([])
      setMessagesLoading(false)
      return undefined
    }
    if (skipNextMessageLoadRef.current === activeConversationId) {
      skipNextMessageLoadRef.current = ''
      setMessagesLoading(false)
      return undefined
    }

    const controller = new AbortController()
    setMessagesLoading(true)
    setError('')
    getAiConversationMessages(activeConversationId, controller.signal)
      .then(({ items = [] } = {}) => {
        const safeItems = Array.isArray(items) ? items : []
        setMessages(safeItems.map(normalizeMessage))
      })
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') {
          setError(requestError.message || '读取 AI 消息失败')
          setMessages([])
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setMessagesLoading(false)
      })

    return () => controller.abort()
  }, [activeConversationId, open, user])

  const createConversation = async ({ skipMessageLoad = false } = {}) => {
    if (!user || conversationActionLoading) return ''

    setConversationActionLoading(true)
    setError('')
    try {
      const result = await createAiConversation()
      const conversation = normalizeConversation(result?.conversation)
      setConversations((current) => upsertConversation(current, conversation))
      if (skipMessageLoad) skipNextMessageLoadRef.current = conversation.id
      setActiveConversationId(conversation.id)
      setMessages([])
      setInput('')
      return conversation.id
    } catch (requestError) {
      setError(requestError.message || '创建 AI 对话失败')
      setConversations([])
      setMessages([])
      return ''
    } finally {
      setConversationActionLoading(false)
    }
  }

  const selectConversation = (conversationId) => {
    if (loading || conversationId === activeConversationId) return
    requestRef.current?.abort()
    setActiveConversationId(conversationId)
    setInput('')
    setError('')
  }

  const removeConversation = async (event, conversationId) => {
    event.stopPropagation()
    if (loading || deletingConversationId) return

    setDeletingConversationId(conversationId)
    setError('')
    try {
      await deleteAiConversation(conversationId)
      setConversations((current) => {
        const safeCurrent = Array.isArray(current) ? current : []
        const next = safeCurrent.filter((item) => item.id !== conversationId)
        if (activeConversationId === conversationId) {
          setActiveConversationId(next[0]?.id || '')
          setMessages([])
          setInput('')
        }
        return next
      })
    } catch (requestError) {
      setError(requestError.message || '删除 AI 对话失败')
      setConversations([])
      setMessages([])
    } finally {
      setDeletingConversationId('')
    }
  }

  const sendMessage = async (value) => {
    const message = String(value || '').trim()
    if (!message || loading) return
    if (!user) {
      setError('请先登录后使用 AI 对话历史')
      return
    }

    const conversationId = activeConversationId
      || await createConversation({ skipMessageLoad: true })
    if (!conversationId) return

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: message,
      recommendedRepos: [],
    }
    setMessages((current) => [...current, userMessage])
    setInput('')
    setError('')
    setLoading(true)
    const controller = new AbortController()
    requestRef.current = controller

    try {
      const result = await sendAiConversationMessage(conversationId, {
        repoId: repoContext?.id,
        repoFullName: repoContext?.fullName,
        message,
        context: {
          ...repoContext,
          type: repoContext?.type || repoContext?.contextType,
          page: assistantPage(location.pathname),
          currentPath: `${location.pathname}${location.search || ''}`,
          currentPageType: assistantPage(location.pathname),
          selectedRepo: repoContext ? {
            id: repoContext.repositoryId || repoContext.id,
            name: repoContext.name,
            fullName: repoContext.fullName,
            githubUrl: repoContext.repoUrl || repoContext.htmlUrl,
            detailPath: repoContext.repositoryId || repoContext.id ? `/repos/${repoContext.repositoryId || repoContext.id}` : '',
          } : null,
          visibleRepos: visibleReposFromPage(),
          userMembership: user?.membership || null,
          quotaSummary: user?.usageSummary || null,
        },
      }, controller.signal)
      if (result?.conversation) {
        setConversations((current) => (
          upsertConversation(current, result?.conversation)
        ))
      }
      setMessages((current) => {
        const safeCurrent = Array.isArray(current) ? current : []
        const nextMessages = safeCurrent.map((item) => (
          item.id === userMessage.id && result?.userMessage
            ? normalizeMessage(result.userMessage)
            : item
        ))
        return [
          ...nextMessages,
          normalizeMessage(result?.assistantMessage || {
            id: `assistant-${Date.now()}`,
            role: 'assistant',
            content: result?.reply,
            actions: Array.isArray(result?.actions) ? result.actions : [],
            recommendedRepos: Array.isArray(result?.recommendedRepos) ? result.recommendedRepos : [],
          }),
        ]
      })
    } catch (requestError) {
      if (requestError.name !== 'AbortError') {
        setError(requestError.message || 'AI 助手暂时不可用，请稍后重试')
        setMessages([])
      }
    } finally {
      if (requestRef.current === controller) requestRef.current = null
      setLoading(false)
    }
  }

  const submit = (event) => {
    event.preventDefault()
    sendMessage(input)
  }

  const handleInputKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      sendMessage(input)
    }
  }

  const runAction = useCallback((action = {}) => {
    const repoUrl = action.repoUrl || action.githubUrl || action.url || ''
    if (action.type === 'external') {
      if (action.url) window.open(action.url, '_blank', 'noopener,noreferrer')
      return
    }
    if (action.type === 'analyze_repo') {
      navigate(`/github-analysis?repo=${encodeURIComponent(repoUrl)}`)
      setOpen(false)
      return
    }
    if (action.type === 'generate_resume') {
      navigate(`/github-analysis?repo=${encodeURIComponent(repoUrl)}&intent=resume`)
      setOpen(false)
      return
    }
    if (action.type === 'generate_interview') {
      navigate(`/github-analysis?repo=${encodeURIComponent(repoUrl)}&intent=interview`)
      setOpen(false)
      return
    }
    if (action.type === 'support') {
      navigate(action.path || '/support')
      setOpen(false)
      return
    }
    if (action.path) {
      navigate(action.path)
      setOpen(false)
    }
  }, [navigate, setOpen])

  const scrollSuggestions = (event) => {
    const container = event.currentTarget
    if (
      Math.abs(event.deltaY) > Math.abs(event.deltaX)
      && container.scrollWidth > container.clientWidth
    ) {
      event.preventDefault()
      container.scrollLeft += event.deltaY
    }
  }

  const startButtonDrag = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    const buttonRect = event.currentTarget.getBoundingClientRect()
    const widgetRect = event.currentTarget
      .closest('.ai-assistant-widget')
      ?.getBoundingClientRect() || buttonRect
    const buttonSize = {
      width: widgetRect.width || buttonRect.width || BUTTON_SIZE,
      height: widgetRect.height || buttonRect.height || BUTTON_SIZE,
    }
    const originPosition = clampButtonPosition({
      x: widgetRect.left,
      y: widgetRect.top,
    }, buttonSize)

    window.clearTimeout(snapTimerRef.current)
    setSnapping(false)

    const drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: originPosition.x,
      originY: originPosition.y,
      moved: false,
      position: originPosition,
      size: buttonSize,
      element: event.currentTarget,
      listeners: {
        move: (pointerEvent) => moveButton(pointerEvent),
        finish: (pointerEvent) => finishButtonDrag(pointerEvent),
        cancel: () => cancelButtonDrag(),
      },
    }
    dragRef.current = drag
    window.addEventListener('pointermove', drag.listeners.move)
    window.addEventListener('pointerup', drag.listeners.finish)
    window.addEventListener('pointercancel', drag.listeners.cancel)
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const moveButton = (event) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return

    const deltaX = event.clientX - drag.startX
    const deltaY = event.clientY - drag.startY
    if (!drag.moved && Math.hypot(deltaX, deltaY) <= 5) {
      return
    }

    event.preventDefault()
    drag.moved = true
    const nextPosition = clampButtonPosition({
      x: drag.originX + deltaX,
      y: drag.originY + deltaY,
    }, drag.size)
    drag.position = nextPosition
    setDragging(true)
    setSnapping(false)
    setButtonPosition(nextPosition)
  }

  const finishButtonDrag = (event) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return

    dragRef.current = null
    removePointerListeners(drag)
    if (drag.element.hasPointerCapture(event.pointerId)) {
      drag.element.releasePointerCapture(event.pointerId)
    }
    if (drag.moved) {
      event.preventDefault()
      const nextPosition = snapButtonPosition(drag.position, drag.size)
      suppressClickRef.current = true
      window.clearTimeout(suppressClickTimerRef.current)
      suppressClickTimerRef.current = window.setTimeout(() => {
        suppressClickRef.current = false
      }, DRAG_CLICK_SUPPRESS_MS)
      setSnapping(true)
      setButtonPosition({
        x: Math.round(nextPosition.x),
        y: Math.round(nextPosition.y),
      })
      window.clearTimeout(snapTimerRef.current)
      snapTimerRef.current = window.setTimeout(() => {
        setSnapping(false)
      }, BUTTON_SNAP_TRANSITION_MS)
    }
    setDragging(false)
  }

  const cancelButtonDrag = () => {
    const drag = dragRef.current
    if (!drag) return
    dragRef.current = null
    removePointerListeners(drag)
    if (drag.element.hasPointerCapture(drag.pointerId)) {
      drag.element.releasePointerCapture(drag.pointerId)
    }
    setDragging(false)
  }

  const startChatWindowDrag = (event) => {
    if (
      isMobile
      || event.button !== 0
      || event.target.closest('button, a, input, textarea')
    ) {
      return
    }
    event.preventDefault()
    const panel = event.currentTarget.closest('.ai-assistant-panel')
    const rect = panel.getBoundingClientRect()
    const drag = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: rect.left,
      originY: rect.top,
      position: { x: rect.left, y: rect.top },
      element: event.currentTarget,
      listeners: {
        move: (pointerEvent) => moveChatWindow(pointerEvent),
        finish: (pointerEvent) => finishChatWindowDrag(pointerEvent),
        cancel: () => cancelChatWindowDrag(),
      },
    }
    chatDragRef.current = drag
    window.addEventListener('pointermove', drag.listeners.move)
    window.addEventListener('pointerup', drag.listeners.finish)
    window.addEventListener('pointercancel', drag.listeners.cancel)
    event.currentTarget.setPointerCapture(event.pointerId)
    setDraggingWindow(true)
  }

  const moveChatWindow = (event) => {
    const drag = chatDragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return

    event.preventDefault()
    const nextPosition = clampChatWindowPosition({
      x: drag.originX + event.clientX - drag.startX,
      y: drag.originY + event.clientY - drag.startY,
    }, chatWindowZoom)
    drag.position = nextPosition
    setChatWindowPosition(nextPosition)
  }

  const finishChatWindowDrag = (event) => {
    const drag = chatDragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return

    chatDragRef.current = null
    removePointerListeners(drag)
    if (drag.element.hasPointerCapture(event.pointerId)) {
      drag.element.releasePointerCapture(event.pointerId)
    }
    saveLocalPreference(CHAT_POSITION_STORAGE_KEY, {
      x: Math.round(drag.position.x),
      y: Math.round(drag.position.y),
    })
    setDraggingWindow(false)
  }

  const cancelChatWindowDrag = () => {
    const drag = chatDragRef.current
    if (!drag) return
    chatDragRef.current = null
    removePointerListeners(drag)
    if (drag.element.hasPointerCapture(drag.pointerId)) {
      drag.element.releasePointerCapture(drag.pointerId)
    }
    setDraggingWindow(false)
  }

  const changeChatWindowZoom = (nextZoom) => {
    if (isMobile || !CHAT_ZOOM_LEVELS.includes(nextZoom)) return
    setChatWindowZoom(nextZoom)
    saveLocalPreference(CHAT_ZOOM_STORAGE_KEY, nextZoom)
    setChatWindowPosition((currentPosition) => {
      const nextPosition = clampChatWindowPosition(
        currentPosition || getDefaultChatWindowPosition(nextZoom),
        nextZoom,
      )
      saveLocalPreference(CHAT_POSITION_STORAGE_KEY, nextPosition)
      return nextPosition
    })
  }

  const stepChatWindowZoom = (direction) => {
    const currentIndex = CHAT_ZOOM_LEVELS.indexOf(chatWindowZoom)
    const nextIndex = Math.min(
      Math.max(0, currentIndex + direction),
      CHAT_ZOOM_LEVELS.length - 1,
    )
    changeChatWindowZoom(CHAT_ZOOM_LEVELS[nextIndex])
  }

  const openAssistant = () => {
    if (suppressClickRef.current || dragRef.current?.moved) return
    if (!isMobile) {
      setChatWindowPosition((currentPosition) => (
        clampChatWindowPosition(
          currentPosition || getDefaultChatWindowPosition(chatWindowZoom),
          chatWindowZoom,
        )
      ))
    }
    setOpen(true)
  }

  const widgetStyle = !isMobile && open
    ? {
        left: (
          chatWindowPosition || getDefaultChatWindowPosition(chatWindowZoom)
        ).x,
        top: (
          chatWindowPosition || getDefaultChatWindowPosition(chatWindowZoom)
        ).y,
        right: 'auto',
        bottom: 'auto',
      }
    : !open && buttonPosition
      ? {
          left: buttonPosition.x,
          top: buttonPosition.y,
          right: 'auto',
          bottom: 'auto',
        }
      : undefined
  const panelStyle = !isMobile ? { zoom: chatWindowZoom } : undefined
  const dragHandlers = { onPointerDown: startButtonDrag }
  const chatWindowDragHandlers = isMobile
    ? {}
    : { onPointerDown: startChatWindowDrag }
  const zoomIndex = CHAT_ZOOM_LEVELS.indexOf(chatWindowZoom)

  return (
    <aside
      className={[
        'ai-assistant-widget',
        open ? 'is-open' : '',
        isMobile ? 'is-mobile' : 'is-desktop',
        dragging ? 'is-dragging' : '',
        snapping ? 'is-snapping' : '',
        draggingWindow ? 'is-dragging-window' : '',
      ].filter(Boolean).join(' ')}
      style={widgetStyle}
    >
      {open && (
        <section
          className="ai-assistant-panel"
          style={panelStyle}
          aria-label="AI 项目助手"
        >
          <header
            className="ai-assistant-header"
            {...chatWindowDragHandlers}
          >
            <AIAssistantAvatar />
            <div className="ai-assistant-title">
              <strong>AI 项目助手</strong>
              <span>
                <i />
                {activeRepoName ? `正在查看 ${activeRepoName}` : '发现项目、理解代码、准备简历'}
              </span>
            </div>
            <div className="ai-assistant-header-actions">
              {!isMobile && (
                <div className="ai-assistant-zoom-control" aria-label="AI 聊天窗口缩放">
                  <button
                    type="button"
                    onClick={() => stepChatWindowZoom(-1)}
                    disabled={zoomIndex === 0}
                    aria-label="缩小 AI 聊天窗口"
                    title="缩小"
                  >
                    <Minus size={14} />
                  </button>
                  <button
                    className="ai-assistant-zoom-reset"
                    type="button"
                    onClick={() => changeChatWindowZoom(DEFAULT_CHAT_ZOOM)}
                    aria-label="恢复 AI 聊天窗口为 100%"
                    title="恢复 100%"
                  >
                    {Math.round(chatWindowZoom * 100)}%
                  </button>
                  <button
                    type="button"
                    onClick={() => stepChatWindowZoom(1)}
                    disabled={zoomIndex === CHAT_ZOOM_LEVELS.length - 1}
                    aria-label="放大 AI 聊天窗口"
                    title="放大"
                  >
                    +
                  </button>
                </div>
              )}
              <button
                className="ai-assistant-close"
                type="button"
                onClick={() => setOpen(false)}
                aria-label="收起 AI 助手"
                title="收起"
              >
                <ChevronDown size={20} />
              </button>
            </div>
          </header>

          <div className="ai-assistant-body">
            {authLoading && (
              <div className="ai-assistant-login-panel" role="status">
                <LoaderCircle className="spin" size={18} />
                <strong>正在整理项目数据</strong>
              </div>
            )}

            {!authLoading && !user && (
              <div className="ai-assistant-login-panel">
                <AIAssistantAvatar />
                <strong>登录后使用 AI 对话历史</strong>
                <p>AI 对话会保存到你的账号，下次登录后可以继续查看。</p>
                <Link
                  className="ai-assistant-login-link"
                  to="/login"
                  onClick={() => setOpen(false)}
                >
                  去登录
                </Link>
              </div>
            )}

            {!authLoading && user && (
              <>
                <aside className="ai-assistant-history" aria-label="AI 对话历史">
                  <div className="ai-assistant-history-head">
                    <span><MessageSquare size={14} /> 历史对话</span>
                    <button
                      type="button"
                      onClick={() => createConversation()}
                      disabled={loading || conversationActionLoading}
                      aria-label="新对话"
                      title="新对话"
                    >
                      {conversationActionLoading
                        ? <LoaderCircle className="spin" size={14} />
                        : <Plus size={15} />}
                      <span>新对话</span>
                    </button>
                  </div>
                  <div className="ai-assistant-history-list">
                    {historyLoading && (
                      <div className="ai-assistant-history-state">
                        <LoaderCircle className="spin" size={14} />
                        读取中
                      </div>
                    )}
                    {!historyLoading && conversations.length === 0 && (
                      <div className="ai-assistant-history-state">
                        <strong>暂无历史对话</strong>
                        <span>点击新对话开始提问</span>
                      </div>
                    )}
                    {!historyLoading && safeConversationGroups.map((group) => {
                      const groupItems = Array.isArray(group?.items) ? group.items : []
                      if (groupItems.length === 0) return null
                      return (
                        <div className="ai-assistant-history-group" key={group.label}>
                          <span>{group.label}</span>
                          {groupItems.map((conversation) => (
                            <div
                              className={[
                                'ai-assistant-history-item',
                                conversation.id === activeConversationId
                                  ? 'is-active'
                                  : '',
                              ].filter(Boolean).join(' ')}
                              key={conversation.id}
                            >
                              <button
                                className="ai-assistant-history-select"
                                type="button"
                                onClick={() => selectConversation(conversation.id)}
                                disabled={loading}
                                aria-current={conversation.id === activeConversationId ? 'true' : undefined}
                                title={conversation.title}
                              >
                                <span>{conversation.title}</span>
                              </button>
                              <button
                                className="ai-assistant-history-delete"
                                type="button"
                                onClick={(event) => removeConversation(event, conversation.id)}
                                disabled={loading || deletingConversationId === conversation.id}
                                aria-label={`删除对话 ${conversation.title}`}
                                title="删除"
                              >
                                {deletingConversationId === conversation.id
                                  ? <LoaderCircle className="spin" size={13} />
                                  : <Trash2 size={13} />}
                              </button>
                            </div>
                          ))}
                        </div>
                      )
                    })}
                  </div>
                </aside>

                <section className="ai-assistant-chat-area">
                  <div
                    className="ai-assistant-messages"
                    ref={messageListRef}
                    aria-live="polite"
                  >
                    {messagesLoading && messages.length === 0 && (
                      <article className="ai-message is-assistant is-loading">
                        <AIAssistantAvatar size="small" />
                        <div><LoaderCircle className="spin" size={17} /> 正在读取历史消息...</div>
                      </article>
                    )}
                    {!messagesLoading && messages.length === 0 && !loading && (
                      <WelcomePanel
                        disabled={loading || messagesLoading}
                        isRepoContext={Boolean(repoContext)}
                        onQuestion={sendMessage}
                        questions={suggestedQuestions}
                        repoName={activeRepoName}
                      />
                    )}
                    {messages.map((message) => (
                      <article
                        className={`ai-message is-${message.role}`}
                        key={message.id}
                      >
                        {message.role === 'assistant' && <AIAssistantAvatar size="small" />}
                        <div className="ai-message-bubble">
                          <MarkdownMessage content={message.content} />
                          <ActionCards actions={message.actions} onAction={runAction} />
                          {message.recommendedRepos?.length > 0 && (
                            <div className="ai-repo-recommendations">
                              {message.recommendedRepos.map((repo) => (
                                <RecommendationCard
                                  key={repo.id}
                                  repo={repo}
                                  onOpen={() => setOpen(false)}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </article>
                    ))}
                    {loading && (
                      <article className="ai-message is-assistant is-loading">
                        <AIAssistantAvatar size="small" />
                        <div>
                          <LoaderCircle className="spin" size={17} />
                          AI 正在思考...
                          <span className="ai-thinking-dots" aria-hidden="true">
                            <i />
                            <i />
                            <i />
                          </span>
                        </div>
                      </article>
                    )}
                  </div>

                  <footer className="ai-assistant-footer">
                    {messages.length > 0 && (
                      <div className="ai-assistant-suggestions">
                        <span><Sparkles size={13} /> 试着问我</span>
                        <div
                          className="ai-assistant-suggestion-list"
                          aria-label="推荐问题，可横向滑动"
                          tabIndex="0"
                          onWheel={scrollSuggestions}
                        >
                          {suggestedQuestions.map((question) => (
                            <button
                              type="button"
                              key={question}
                              onClick={() => sendMessage(question)}
                              disabled={loading || messagesLoading}
                            >
                              {question}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {error && (
                      <div className="ai-assistant-error" role="alert">
                        <span>{error}</span>
                        <button type="button" onClick={() => setError('')} aria-label="关闭错误">
                          <X size={15} />
                        </button>
                      </div>
                    )}

                    <form className="ai-assistant-composer" onSubmit={submit}>
                      <textarea
                        value={input}
                        onChange={(event) => setInput(event.target.value)}
                        onKeyDown={handleInputKeyDown}
                        placeholder={repoContext ? '问问这个项目的技术亮点...' : '描述你想找的开源项目...'}
                        aria-label="向 AI 助手提问"
                        rows="2"
                        maxLength="1000"
                        disabled={loading || messagesLoading}
                      />
                      <button
                        type="submit"
                        disabled={loading || messagesLoading || !input.trim()}
                        aria-label="发送问题"
                        title="发送"
                      >
                        {loading ? <LoaderCircle className="spin" size={18} /> : <Send size={18} />}
                      </button>
                    </form>
                  </footer>
                </section>
              </>
            )}
          </div>

        </section>
      )}

      {!open && !hideFloatingButton && (
        <button
          className="ai-assistant-fab"
          type="button"
          onClick={openAssistant}
          {...dragHandlers}
          aria-label="打开 AI 开源项目助手"
          aria-expanded="false"
          title={isMobile ? '打开 AI 开源项目助手' : '拖动调整位置，点击打开 AI 助手'}
        >
          <AIAssistantAvatar />
          <span>问 AI</span>
        </button>
      )}
    </aside>
  )
}

export default AIAssistantWidget
